/**
 * The loan register.
 *
 * WHAT KIND OF SCREEN THIS IS. An internal finance tool, opened to answer "what
 * do we owe and when". It is scanned and operated, not read and remembered, so
 * the craft goes into information density, scannability and making each row's
 * state legible cold. Deliberately not striking: a liability register that
 * draws attention to itself rather than to its numbers is the wrong shape.
 *
 * THE SHAPE, and where it comes from. Xero, QuickBooks and Exact all present a
 * liability register the same way: a list with one row per position, the
 * balance and the next payment on the row, and the detail behind an expansion
 * rather than a navigation. That convergence is the reason for this layout. The
 * thing none of them do, and this page must, is say WHEN its data is from -
 * because the bank import here ends weeks before today and the arithmetic
 * silently assumed otherwise.
 *
 * THE ONE RULE THIS PAGE EXISTS TO HONOUR. Interest was first paid on 26
 * October 2026 and the import ends 30 September, so a register that read "no
 * payment recorded" as "not paid" would open by accusing the owner of arrears
 * on money he has paid. Every payment carries one of five states and two of
 * them are explicitly "we cannot see this", with the account and the date that
 * limit the claim named on screen.
 *
 * State is carried by a GLYPH and a word as well as a colour, so it survives a
 * greyscale print and colour blindness.
 */

import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock,
  EyeOff,
  Landmark,
  Search,
} from "lucide-react";

import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../lib/api";

/**
 * How each payment state reads.
 *
 * `icon` and `label` carry the meaning; `tone` only reinforces it. The two
 * "cannot see" states are deliberately neutral rather than warning-coloured:
 * they are not a problem with the loan, they are a gap in the data, and
 * colouring them red would make the page cry wolf on every run until an import
 * catches up.
 */
const PAYMENT_STATES = {
  paid: {
    icon: Check,
    label: "Paid",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
    explain: "A payment on the bank statement is allocated to this period.",
  },
  scheduled: {
    icon: CalendarClock,
    label: "Scheduled",
    tone: "border-slate-200 bg-slate-50 text-slate-600",
    explain: "Not due yet.",
  },
  due_data_ends_before: {
    icon: EyeOff,
    label: "Cannot tell yet",
    tone: "border-slate-300 bg-slate-100 text-slate-700",
    explain:
      "This was due after the last bank statement we have loaded, so we cannot say whether it was paid.",
  },
  due_unallocated_candidate: {
    icon: Search,
    label: "Payment to match",
    tone: "border-amber-200 bg-amber-50 text-amber-800",
    explain:
      "A payment to this lender is on the statement but has not been linked to this period yet.",
  },
  overdue: {
    icon: AlertTriangle,
    label: "Overdue",
    tone: "border-red-200 bg-red-50 text-red-700",
    explain:
      "The statement covers this date and no payment to this lender matches it.",
  },
};

/**
 * A sentence a person can read, from whatever the server sent.
 *
 * FastAPI answers a validation error with `detail` as a LIST of objects, not
 * a string. Putting that straight into the tree throws "Objects are not valid
 * as a React child", and this app has no error boundary anywhere, so the
 * whole page goes white and whatever was typed is lost.
 */
function readProblem(error, fallback = "That could not be saved, so nothing has been recorded.") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const said = detail
      .map((item) => (typeof item?.msg === "string" ? item.msg : null))
      .filter(Boolean)
      .join(". ");
    if (said) return said;
  }
  return fallback;
}

function money(amount, currency = "EUR") {
  if (amount === null || amount === undefined) return "not recorded";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function plainDate(value) {
  if (!value) return "not recorded";
  // Tolerant of a full timestamp as well as a plain date: `paid_on` and
  // `test_date` are stored as free strings and one of them reaches the page
  // without passing through the backend's date normaliser. An Invalid Date
  // makes Intl throw, and with no error boundary in this app that is a blank
  // white page rather than a bad cell.
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function PaymentStateBadge({ state }) {
  const shape = PAYMENT_STATES[state];
  if (!shape) {
    return (
      <Badge variant="outline" className="whitespace-nowrap">
        <CircleHelp className="mr-1 h-3 w-3" aria-hidden="true" />
        {state}
      </Badge>
    );
  }
  const Icon = shape.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${shape.tone}`}
      title={shape.explain}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {shape.label}
    </span>
  );
}

/** A figure with the date it is true as of, because none of them is live. */
function Figure({ label, value, note }) {
  return (
    <div className="min-w-0">
      <div className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
        {value}
      </div>
      {note ? <div className="mt-1 text-xs text-slate-500">{note}</div> : null}
    </div>
  );
}

/**
 * Record a payment the bank data cannot show.
 *
 * This is the feature's central case and it had no affordance at all: the
 * backend endpoint existed, the page described the behaviour, and nobody
 * could actually record the 26 October interest. A register that explains why
 * it cannot see a payment, and then offers no way to tell it, is only half
 * the rule.
 *
 * It does NOT mark the period paid. An assertion sits beside the schedule and
 * the disagreement stays visible when the statement arrives.
 */
function RecordPaymentDialog({ loan, open, onClose, onRecorded, user }) {
  const [paidOn, setPaidOn] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);

  const save = async () => {
    setSaving(true);
    setProblem(null);
    try {
      await apiService.assertLoanPayment({
        loan_id: loan.id,
        paid_on: paidOn,
        amount: Number(amount),
        // The person actually recording it. "owner" was hardcoded, so a page
        // built around provenance displayed one it had not verified.
        asserted_by: user?.name || user?.email || "unknown",
        asserted_at: new Date().toISOString(),
      });
      onRecorded();
      onClose();
    } catch (error) {
      setProblem(readProblem(error));
    } finally {
      setSaving(false);
    }
  };

  // `!"0"` is false, so a zero got past an emptiness check and was refused
  // by the server instead of by the form.
  const incomplete = !paidOn || !(Number(amount) > 0);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record a payment to {loan.lender_name}</DialogTitle>
          <DialogDescription>
            For a payment you have made that is not on a loaded bank statement
            yet. It is kept beside the schedule rather than treated as settling
            it, so when the statement arrives any disagreement stays visible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="paid-on">Date paid</Label>
            <Input
              id="paid-on"
              type="date"
              value={paidOn}
              onChange={(event) => setPaidOn(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amount">Amount in {loan.currency}</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          {problem ? (
            <Alert variant="destructive">
              <AlertDescription>{problem}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={incomplete || saving}>
            {saving ? "Recording" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoanRow({ loan, expanded, onToggle, onRecordPayment, canWrite }) {
  const next = loan.next_payment;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <>
      <tr className="border-b border-slate-100 align-top hover:bg-slate-50">
        <td className="py-3 pl-2 pr-3">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex items-start gap-2 text-left"
          >
            <Chevron
              className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
              aria-hidden="true"
            />
            <span>
              <span className="font-medium text-slate-900">{loan.lender_name}</span>
              <span className="block text-xs text-slate-500">
                {loan.agreement_name}
              </span>
            </span>
          </button>
        </td>
        <td className="whitespace-nowrap py-3 pr-3 text-right tabular-nums text-slate-900">
          {money(loan.outstanding, loan.currency)}
        </td>
        <td className="whitespace-nowrap py-3 pr-3 text-right tabular-nums text-slate-600">
          {(loan.annual_rate * 100).toFixed(2)}%
        </td>
        <td className="whitespace-nowrap py-3 pr-3 text-right tabular-nums text-slate-600">
          {money(loan.accrued_interest, loan.currency)}
        </td>
        <td className="whitespace-nowrap py-3 pr-3 text-slate-600">
          {next ? (
            <span className="flex items-center gap-2">
              <span className="tabular-nums">{plainDate(next.end)}</span>
              <PaymentStateBadge state={next.state} />
            </span>
          ) : (
            "nothing scheduled"
          )}
        </td>
        <td className="whitespace-nowrap py-3 pr-2 text-slate-600">
          {loan.end_state === "convertible" ? "Convertible" : "Repay"}
          {loan.obligor?.status === "transferred" ? (
            <span className="ml-2 text-xs text-slate-500">
              owed by {loan.obligor.party}
            </span>
          ) : null}
        </td>
      </tr>

      {expanded ? (
        <tr className="border-b border-slate-100 bg-slate-50/60">
          <td colSpan={6} className="px-6 py-4">
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <h4 className="text-sm font-semibold text-slate-900">
                  What we cannot say about this loan
                </h4>
                {loan.unknowns?.length ? (
                  <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                    {loan.unknowns.map((gap) => (
                      <li key={gap} className="flex gap-2">
                        <CircleHelp
                          className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                          aria-hidden="true"
                        />
                        <span>{gap}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-slate-600">
                    Every term is recorded from the signed agreement.
                  </p>
                )}

                {/*
                  Admins only, matching the backend. Showing it to a manager
                  who can read the register would be a button that always
                  fails, which reads as a broken page rather than a permission.
                */}
                {canWrite ? (
                  <div className="mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRecordPayment(loan)}
                    >
                      Record a payment you have made
                    </Button>
                  </div>
                ) : null}

                {loan.asserted_payments?.length ? (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Payments recorded by hand
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">
                      Told to us directly and not yet on a loaded statement. Kept
                      beside the schedule rather than treated as settling it, so a
                      statement that disagrees stays visible.
                    </p>
                    <ul className="mt-2 space-y-1 text-sm tabular-nums text-slate-700">
                      {loan.asserted_payments.map((payment) => (
                        <li key={payment.id}>
                          {plainDate(payment.paid_on)}
                          {"  "}
                          {money(payment.amount, loan.currency)}
                          <span className="ml-2 text-xs text-slate-500">
                            per {payment.asserted_by}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-slate-900">Schedule</h4>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full min-w-[30rem] text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="whitespace-nowrap py-1 pr-3 font-medium">Period to</th>
                        <th className="whitespace-nowrap py-1 pr-3 text-right font-medium">
                          Interest
                        </th>
                        <th className="whitespace-nowrap py-1 pr-3 text-right font-medium">
                          Principal
                        </th>
                        <th className="whitespace-nowrap py-1 font-medium">State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loan.periods.slice(0, 12).map((period) => (
                        <tr key={period.end} className="border-t border-slate-200">
                          <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums text-slate-700">
                            {plainDate(period.end)}
                            {period.is_opening_stub ? (
                              <span className="ml-1 text-xs text-slate-500">
                                (part period)
                              </span>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-slate-700">
                            {money(period.interest_due, loan.currency)}
                          </td>
                          <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-slate-700">
                            {period.principal_due
                              ? money(period.principal_due, loan.currency)
                              : ""}
                          </td>
                          <td className="py-1.5">
                            <PaymentStateBadge state={period.state} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {loan.periods.length > 12 ? (
                  <p className="mt-2 text-xs text-slate-500">
                    The first 12 of {loan.periods.length} periods, to the maturity
                    date on the agreement.
                  </p>
                ) : null}
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export default function LoansPage() {
  const [register, setRegister] = useState(null);
  const [liability, setLiability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [recording, setRecording] = useState(null);
  const { user } = useAuth();
  // Writing a loan term or a payment is admin-only on the backend, so the
  // control is admin-only here too.
  const canWrite = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    setProblem(null);
    try {
      const [registerResponse, liabilityResponse] = await Promise.all([
        apiService.getLoanRegister(),
        apiService.getLoanLiability(),
      ]);
      setRegister(registerResponse.data);
      setLiability(liabilityResponse.data);
    } catch (error) {
      // Said in full rather than as a generic failure: a blank page and a 403
      // read the same to somebody who cannot tell which they got.
      setProblem(
        readProblem(error, "The loan register could not be loaded. Nothing has been changed."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (problem) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>{problem}</span>
            <Button size="sm" variant="outline" onClick={load}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const loans = register?.loans ?? [];
  const currencies = Object.entries(liability?.by_currency ?? {});
  // The staleness bound that limits what this page may claim. Reported from
  // the earliest account, because a total that adds a stale account is only as
  // current as its stalest part.
  // The earliest bound across the accounts that actually SERVICE these loans,
  // which the backend now publishes per loan. Taking a global minimum over
  // every account in the book named a date from an account the debt is not
  // paid from, under a sentence claiming it was the servicing account.
  const servicingDates = loans
    .map((loan) => loan.servicing_data_ends)
    .filter(Boolean);
  const completeTo = servicingDates.length ? servicingDates.slice().sort()[0] : null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900">
            <Landmark className="h-6 w-6 text-slate-400" aria-hidden="true" />
            Loans
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Money borrowed and what it costs. Every figure here is as at the last
            bank statement loaded, not today.
          </p>
        </div>
      </div>

      {completeTo ? (
        <Alert className="border-slate-300 bg-slate-50">
          <Clock className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="text-slate-700">
            Bank data is loaded to {plainDate(completeTo)} on the account used to
            service this debt. Anything due after that date shows as{" "}
            <span className="font-medium">cannot tell yet</span>: it may well have
            been paid, and this page will not say otherwise until the statement
            arrives.
          </AlertDescription>
        </Alert>
      ) : null}

      {loans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Landmark
              className="mx-auto h-10 w-10 text-slate-300"
              aria-hidden="true"
            />
            <h2 className="mt-4 text-lg font-semibold text-slate-900">
              No loans recorded yet
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">
              Shareholder loans already on the bank statement are counted as
              funding received until they are recorded here. Loans are entered
              through the API or the backfill script for now; once they exist,
              this page shows what is owed, when each payment falls due, and
              which terms are still missing from the paperwork.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/*
            One card per currency, each laying its figures out along a row.
            An earlier version stacked them in a narrow column inside a
            four-across grid, so with a single currency three quarters of the
            row was empty and "Due within a year" sat hard against "Later"
            with the two amounts almost touching. Looking at the render is
            what caught it.
          */}
          <div className="grid gap-4 xl:grid-cols-2">
            {currencies.map(([currency, totals]) => (
              <Card key={currency}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-slate-600">
                    Owed in {currency}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-x-10 gap-y-5">
                    <Figure
                      label="Outstanding"
                      value={money(totals.outstanding, currency)}
                    />
                    <Figure
                      label="Due within a year"
                      value={money(totals.current, currency)}
                    />
                    <Figure
                      label="Later"
                      value={money(totals.non_current, currency)}
                    />
                    {totals.unknown_timing ? (
                      <Figure
                        label="Timing unknown"
                        value={money(totals.unknown_timing, currency)}
                        note="No maturity recorded, so this sits in neither column."
                      />
                    ) : null}
                    <Figure
                      label="Interest owed, not invoiced"
                      value={money(totals.accrued_interest, currency)}
                    />

                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {liability?.obligor_tests_unassessed?.length ? (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription className="text-amber-900">
                An agreement says this debt moves to another party if a condition
                is met, and the date to check has passed with no answer recorded.
                Until somebody records it, these amounts are still shown as owed
                by the company:{" "}
                {liability.obligor_tests_unassessed
                  .map(
                    (entry) =>
                      `${entry.lender_name} (${money(entry.outstanding, entry.currency)}, to check ${plainDate(entry.test_date)})`,
                  )
                  .join(", ")}
                .
              </AlertDescription>
            </Alert>
          ) : null}

          {Object.entries(liability?.off_company_outstanding_by_currency ?? {}).map(
            ([currency, amount]) => (
              <Alert key={currency} className="border-slate-300 bg-slate-50">
                <AlertDescription className="text-slate-700">
                  {money(amount, currency)} of the outstanding total above has
                  moved to another party under its agreement, so it is still owed
                  but no longer by the company. It is left out of the
                  due-within-a-year and later columns.
                </AlertDescription>
              </Alert>
            ),
          )}

          {register?.orphaned_loans?.length ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {register.orphaned_loans.length} loan(s) point at an agreement
                that no longer exists, so their terms cannot be read and they are
                left out of every figure on this page:{" "}
                {register.orphaned_loans.map((row) => row.lender_name).join(", ")}
                .
              </AlertDescription>
            </Alert>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {loans.length} loan{loans.length === 1 ? "" : "s"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[52rem] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="whitespace-nowrap py-2 pl-2 pr-3 font-medium">
                        Lender
                      </th>
                      <th className="whitespace-nowrap py-2 pr-3 text-right font-medium">
                        Outstanding
                      </th>
                      <th className="whitespace-nowrap py-2 pr-3 text-right font-medium">
                        Rate
                      </th>
                      <th className="whitespace-nowrap py-2 pr-3 text-right font-medium">
                        Interest owed
                      </th>
                      <th className="whitespace-nowrap py-2 pr-3 font-medium">
                        Next payment
                      </th>
                      <th className="whitespace-nowrap py-2 pr-2 font-medium">
                        Ends by
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loans.map((loan) => (
                      <LoanRow
                        key={loan.id}
                        loan={loan}
                        expanded={expanded.has(loan.id)}
                        onToggle={() => toggle(loan.id)}
                        onRecordPayment={setRecording}
                        canWrite={canWrite}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {recording ? (
        <RecordPaymentDialog
          loan={recording}
          open
          onClose={() => setRecording(null)}
          onRecorded={load}
          user={user}
        />
      ) : null}
    </div>
  );
}
